import React, { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import { Box, TextField, Typography } from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import axios from "axios";

// Fix for default marker icons
L.Icon.Default.mergeOptions({
    iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
    shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

type MapFieldProps = {
    label?: string;
    value?: any;
    onChange: any;
    address?: string; // For edit mode - show address in search bar when coordinates are loaded
};

const fetchSuggestions = async (query: string) => {
    if (!query || query.trim().length < 2) return [];
    try {
        const response = await axios.get(
            `https://nominatim.openstreetmap.org/search`,
            {
                params: {
                    q: query,
                    format: "json",
                    addressdetails: 1,
                    limit: 5,
                }
            }
        );
        return response.data;
    } catch (error) {
        console.error("Error fetching location suggestions:", error);
        return [];
    }
};

// Component to pan map when coordinates change (e.g., from search selection)
const ChangeView = ({ center, zoom }: { center: [number, number]; zoom: number }) => {
    const map = useMap();
    useEffect(() => {
        map.flyTo(center, zoom, { duration: 0.2 });
    }, [center[0], center[1], zoom, map]);
    return null;
};

// Normalize coordinates - handle both { lat, lng } and { latitude, longitude }
const normalizeCoords = (val: any): { lat: number; lng: number } | null => {
    if (!val) return null;
    const lat = val.lat ?? val.latitude;
    const lng = val.lng ?? val.longitude;
    if (typeof lat === "number" && typeof lng === "number") {
        return { lat, lng };
    }
    return null;
};

const MapField: React.FC<MapFieldProps> = ({ label = "Select Location", value, onChange, address }) => {
    const normalizedValue = normalizeCoords(value);
    const [markerPosition, setMarkerPosition] = useState<any>(normalizedValue || { lat: 28.6139, lng: 77.209 });
    const [searchQuery, setSearchQuery] = useState("");
    const [suggestions, setSuggestions] = useState<any[]>([]);

    useEffect(() => {
        const coords = normalizeCoords(value);
        if (coords) {
            setMarkerPosition(coords);
        }
    }, [value]);

    // Sync search bar with address when editing (address prop + coordinates loaded)
    useEffect(() => {
        if (address && normalizedValue) {
            setSearchQuery((prev) => prev || address);
        }
    }, [address, normalizedValue?.lat, normalizedValue?.lng]);

    const handleSearchInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const query = e.target.value;
        setSearchQuery(query);
        const results = await fetchSuggestions(query);
        setSuggestions(results);
    };

    const handleSuggestionClick = (location: any) => {
        if (location && location.lat && location.lon) {
            const coords = {
                lat: parseFloat(location.lat),
                lng: parseFloat(location.lon),
            };
            setMarkerPosition(coords);
            onChange(coords, location.display_name || "");
            setSearchQuery(location.display_name || "");
            setSuggestions([]);
        }
    };

    const LocationMarker = () => {
        useMapEvents({
            click: (e: any) => {
                const newCoordinates = { lat: e.latlng?.lat, lng: e.latlng?.lng };
                setMarkerPosition(newCoordinates);
                onChange(newCoordinates);
            },
        });

        const customIcon = L.divIcon({
            html: `<div style="color: red; font-size: 24px; text-align: center;">
               <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="red">
                 <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z"/>
               </svg>
             </div>`,
            className: "custom-leaflet-icon",
            iconSize: [24, 24],
            iconAnchor: [12, 24],
            popupAnchor: [0, -24],
        });

        return <Marker position={markerPosition} icon={customIcon} />;
    };

    const center: [number, number] = markerPosition
        ? [markerPosition.lat, markerPosition.lng]
        : [28.6139, 77.209];

    return (
        <Box>
            <Typography variant="h6" gutterBottom>{label}</Typography>
            <TextField
                placeholder="Search location (e.g., city, address, landmark)"
                value={searchQuery}
                onChange={handleSearchInput}
                variant="outlined"
                size="small"
                sx={{
                    marginBottom: 1,
                    width: { xs: "100%", sm: "100%", md: "50%" },
                    maxWidth: "100%",
                    boxSizing: "border-box",
                }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon color="action" />
                        </InputAdornment>
                    ),
                }}
            />
            {suggestions.length > 0 && (
                <Box
                    sx={{
                        width: { xs: "100%", sm: "100%", md: "50%" },
                        maxWidth: "100%",
                        boxSizing: "border-box",
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        maxHeight: 200,
                        overflowY: "auto",
                        marginBottom: 1,
                        backgroundColor: "background.paper",
                    }}
                >
                    {suggestions.map((suggestion: any, index: number) => (
                        <Box
                            key={index}
                            onClick={() => handleSuggestionClick(suggestion)}
                            sx={{
                                padding: 1.5,
                                cursor: "pointer",
                                "&:hover": { backgroundColor: "action.hover" },
                            }}
                        >
                            <Typography variant="body2">
                                {suggestion?.display_name}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}
            <MapContainer
                center={center}
                zoom={13}
                style={{ height: "400px", width: "100%", marginBottom: "16px" }}
            >
                <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />
                <ChangeView center={center} zoom={13} />
                <LocationMarker />
            </MapContainer>
        </Box>
    );
};

export default MapField;
